<script lang="ts">
	import { inputFieldFormats, markFieldLabelRequired } from '../utils/formatters'
	import 'flatpickr/dist/flatpickr.css'
	import 'flatpickr/dist/themes/light.css'
	import Flatpickr from './datepicker/Flatpickr.svelte'
	import dayjs from 'dayjs'
	import type { FontProps } from '../types/fontProps'
	import { DefaultInputFieldProps, getFontPropsVarString } from '../types/fontProps'
	import MaterialDatePicker from '../molecules/MaterialDatePicker.svelte'
	import { getContext, hasContext } from 'svelte'
	import { AnimatePresence, AnimateSharedLayout, Motion } from 'svelte-motion'
	import { openCloseVariants } from '../utils/svelteMotionUtils'
	import {
		getExactPathErrorMessage,
		getPathErrorMessage,
		isExactPathInErrorList,
		isPathInErrorList
	} from '../utils/formValidation'
	import { German } from 'flatpickr/dist/l10n/de.js'
	import type { Writable } from 'svelte/store'
	import { writable } from 'svelte/store'
	import type { FormError } from '../types/formError'
	import { DatePickerMode, type UnixDateRange } from '../types/datepicker'
	import type { Options } from 'flatpickr/dist/types/options'

	const now = new Date()

	interface DatePickerInputProps {
		id?: string
		label?: string
		date?: number | UnixDateRange | null
		mode?: DatePickerMode
		format?: { maxWidth: string; [key: string]: unknown }
		fontProps?: FontProps
		placeholder?: string
		materialWidth?: string
		disabled?: boolean
		required?: boolean
		disableDays?: (date: Date) => boolean
		maxDate?: Date
		minDate?: Date
		appendTo?: HTMLElement
		errorPath?: string
		onchange?: (event: unknown) => void
	}

	let {
		id = '',
		label = 'Date',
		date = $bindable<number | UnixDateRange | null | undefined>(),
		mode = DatePickerMode.DATE,
		format = inputFieldFormats.EXPANDED,
		fontProps = DefaultInputFieldProps(),
		placeholder = 'Datum auswählen',
		materialWidth = '100%',
		disabled = false,
		required = false,
		disableDays = undefined,
		maxDate = new Date(now.getFullYear() + 5, 11, 31),
		minDate = new Date(now.getFullYear() - 5, 0, 1),
		appendTo = undefined,
		errorPath = '',
		onchange
	}: DatePickerInputProps = $props()

	/* Handling of the error message indication with a context store that is set on the parent including the form */
	let formErrors: Writable<FormError[]> = hasContext('formErrors')
		? getContext('formErrors')
		: writable([])

	const useMaterialDesign: boolean = hasContext('useMaterialDesign')
		? getContext('useMaterialDesign')
		: false

	// eslint-disable-next-line @typescript-eslint/no-explicit-any -- temporary: MaterialDatePicker expects string, Flatpickr expects DateOption; tighten after Task 8 migration
	let internalValue: any = $state(undefined)
	let open = $state(false) // toggles the border when the calendar is opened
	const toggleOpen = () => (open = !open)

	// change the range separator to only show a '-' instead of 'bis'
	German.rangeSeparator = ' - '

	const error = $derived.by(() => {
		if (errorPath && errorPath !== '' && $formErrors?.length > 0) {
			let paths = errorPath.includes('||') ? errorPath.split('||') : [errorPath]
			let result = ''
			paths.forEach((path) => {
				if (path.includes('.*')) {
					result += isPathInErrorList($formErrors, path)
						? getPathErrorMessage($formErrors, path)
						: ''
				} else {
					result += isExactPathInErrorList($formErrors, path)
						? getExactPathErrorMessage($formErrors, path)
						: ''
				}
			})
			return result
		}
		return ''
	})

	const flatpickrOptions: Options = $derived({
		dateFormat: getTimeFormat(),
		time_24hr: true,
		maxDate,
		minDate,
		clickOpens: !disabled,
		weekNumbers: true,
		locale: German,
		appendTo: appendTo,
		noCalendar: mode === DatePickerMode.TIME,
		enableTime: mode === DatePickerMode.TIME || mode === DatePickerMode.DATETIME,
		disable: [disableDays as (date: Date) => boolean],
		// flatpickr only knows single/range/multiple – TIME and DATETIME are both 'single' with
		// noCalendar/enableTime flags controlling the picker shape
		mode: (mode === DatePickerMode.DATETIME || mode === DatePickerMode.TIME
			? DatePickerMode.DATE
			: mode) as 'single' | 'range' | 'multiple' | 'time'
	})

	const displayLabel = $derived(required ? markFieldLabelRequired(label) : label)

	function setInternalValueToDate() {
		if (mode === DatePickerMode.DATE) {
			internalValue = date ? dayjs.unix(date as number).format('DD.MM.YYYY') : ''
		} else if (mode === DatePickerMode.TIME) {
			internalValue = date ? dayjs.unix(date as number).format('HH:mm') : ''
		} else if (mode === DatePickerMode.DATETIME) {
			internalValue = date ? dayjs.unix(date as number).format('DD.MM.YYYY HH:mm') : ''
		} else {
			const [from, to] = (date as UnixDateRange) ?? ['', '']
			internalValue =
				from && to
					? [dayjs.unix(from).format('DD.MM.YYYY'), dayjs.unix(to).format('DD.MM.YYYY')]
					: ['', '']
		}
	}

	$effect(() => {
		// Track `date` to re-run when it changes
		void date
		setInternalValueToDate()
	})

	/**
	 * Handles date changes from both:
	 * - New Flatpickr.svelte (callback: selectedDates, dateStr, instance)
	 * - MaterialDatePicker (Svelte 4 compat: CustomEvent with detail = [selectedDates, dateStr, instance])
	 *
	 * Fires only on user selection. Programmatic value updates via setInternalValueToDate
	 * do not trigger onChange in the Svelte 5 Flatpickr wrapper (setDate is called with
	 * triggerChange=false), so no init-time guard is needed.
	 */
	function onDatePickerChanged(selectedDatesOrEvent: Date[] | CustomEvent) {
		// Normalize: extract selectedDates from either call signature
		const selectedDates: Date[] =
			selectedDatesOrEvent instanceof CustomEvent
				? selectedDatesOrEvent.detail[0]
				: selectedDatesOrEvent

		if (mode === DatePickerMode.DATE) {
			date = selectedDates[0] ? dayjs(selectedDates[0]).unix() : null
			onchange?.(selectedDates)
		} else if (mode === DatePickerMode.RANGE) {
			const [from, to] = selectedDates
			if (from && to) {
				date = [dayjs(from).unix(), dayjs(to).unix()]
				onchange?.(selectedDates)
			}
		} else if (mode === DatePickerMode.MULTIPLE_DATES) {
			date = [...selectedDates.map((d) => dayjs(d).unix())] as unknown as UnixDateRange
			onchange?.(selectedDates)
		} else if (mode === DatePickerMode.TIME || mode === DatePickerMode.DATETIME) {
			// flatpickr reports the selected time as a Date on the current day
			// guard the empty selection (field cleared) so dayjs(undefined) doesn't default to "now"
			date = selectedDates[0] ? dayjs(selectedDates[0]).unix() : null
			onchange?.(selectedDates)
		}
	}

	function getTimeFormat() {
		if (mode === DatePickerMode.TIME) {
			return 'H:i'
		} else if (mode === DatePickerMode.DATETIME) {
			return 'd.m.Y H:i'
		}
		return 'd.m.Y'
	}
</script>

{#if useMaterialDesign}
	<MaterialDatePicker
		{id}
		bind:value={internalValue}
		label={displayLabel}
		{placeholder}
		{flatpickrOptions}
		{disabled}
		{fontProps}
		width={materialWidth}
		{error}
		onchange={onDatePickerChanged}
		onopen={toggleOpen}
		onclose={toggleOpen}
	/>
{:else}
	<AnimateSharedLayout>
		<div class="input-field-error-message-container">
			<div class="labeled-input-field" class:fullWidth={format === inputFieldFormats.FULL}>
				{#if displayLabel && displayLabel !== '' && displayLabel !== 'undefined'}
					<span>{displayLabel}</span>
				{/if}
				<div
					class="input-field-focus date-time-field input"
					class:open
					style="width: {format.maxWidth}; max-width: {format.maxWidth};{getFontPropsVarString(
						fontProps
					)}"
				>
					<Flatpickr
						{id}
						bind:value={internalValue}
						{placeholder}
						options={flatpickrOptions}
						onchange={onDatePickerChanged}
						onopen={toggleOpen}
						onclose={toggleOpen}
					/>
				</div>
			</div>
			<AnimatePresence list={error && error !== '' ? [{ key: 1 }] : []}>
				<Motion
					let:motion={collapse}
					initial="collapsed"
					animate="open"
					exit="collapsed"
					variants={openCloseVariants}
				>
					<span class="form-field-error" use:collapse>{@html error}</span>
				</Motion>
			</AnimatePresence>
		</div>
	</AnimateSharedLayout>
{/if}

<style lang="scss">
	@import '$styles/global';
	@import '$styles/inputFields';

	.date-time-field .input {
		@extend .input-field;

		height: 2rem;
		max-height: 2rem;
		background-color: white !important;
	}

	div :global(.flatpickr-input) {
		border: none;
		box-shadow: none;
		height: 1rem;
		padding: 0;
		width: 100%; /* we set the width to 100% such that the placeholder is as long as the container */

		&::placeholder {
			color: var(--placeholder-color);
		}
	}

	:global(.flatpickr-day.selected),
	:global(.flatpickr-day.endRange) {
		background: $primaryColor !important;
		border-color: $primaryColor !important;
	}

	.fullWidth :global(.flatpickr-input) {
		width: 100%;
	}

	.open {
		border: 1px solid $primaryColor;
		outline: none !important;
	}

	:global(.flatpickr-calendar.animate.open) {
		z-index: 1000003;
	}
</style>
